import { useCallback } from 'react';
import { LineItem } from 'shared/types/wishlist/LineItem';
import { Wishlist } from 'shared/types/wishlist/Wishlist';
import useSWR, { mutate, SWRResponse } from 'swr';
import { sdk } from 'sdk';
import { revalidateOptions, useAccount, useCart } from 'frontastic';
import { Variant } from 'shared/types/product';

const useWishlist = () => {
  const extensions = sdk.composableCommerce;
  const { account } = useAccount();
  const { data: cartData } = useCart();

  const result = useSWR(
    ['/action/wishlist/getWishlist', account?.accountId, cartData?.cartId],
    () => extensions.wishlist.getWishlist(),
    { ...revalidateOptions, revalidateOnFocus: false },
  );
  const data = (result.data?.isError ? {} : { data: result.data?.data }) as SWRResponse<Wishlist>;

  const totalWishlistItems = data.data?.lineItems?.reduce((acc, curr) => acc + (curr.count as number), 0) ?? 0;

  const addToWishlist = useCallback(async (sku: Variant['sku'], count = 1) => {
    const extensions = sdk.composableCommerce;

    const res = await extensions.wishlist.addItem({ variant: { sku }, count });

    if (res.isError) {
      res.error.message || 'Something went wrong';
    }

    mutate(['/action/wishlist/getWishlist', account?.accountId, cartData?.cartId]);

    return true;
  }, []);

  const removeLineItem = useCallback(async (wishlist: Wishlist, lineItem: Partial<LineItem>) => {
    const extensions = sdk.composableCommerce;

    const newWishlist = {
      ...wishlist,
      lineItems: wishlist.lineItems?.filter((item) => item.lineItemId !== lineItem.lineItemId) ?? [],
    };

    const res = extensions.wishlist.removeItem({ lineItem: { id: lineItem.lineItemId as string } });
    await mutate(['/action/wishlist/getWishlist', account?.accountId, cartData?.cartId], res, {
      optimisticData: { data: newWishlist },
      rollbackOnError: true,
    });
  }, []);

  const deleteWishlist = useCallback(async (wishlist: Wishlist) => {
    const res = await sdk.callAction({ actionName: 'wishlist/deleteWishlist' });
    const newWishlist = {
      ...wishlist,
      lineItems: [],
    };

    if (!res.isError) {
      await mutate(['/action/wishlist/getWishlist', account?.accountId, cartData?.cartId], res.data, {
        optimisticData: { data: newWishlist },
        rollbackOnError: true,
      });
    }
  }, []);

  const shareWishlist = useCallback(async (email: string, message: string, shareUrl: string) => {
    return extensions.wishlist.shareWishlist({ email, message, shareUrl });
  }, []);

  const updateLineItem = useCallback(async (wishlist: Wishlist, lineItem: LineItem, count = 1) => {
    const extensions = sdk.composableCommerce;

    const newWishlist = {
      ...wishlist,
      lineItems:
        wishlist.lineItems?.map((item) => {
          if (item.lineItemId === lineItem.lineItemId) {
            return { ...item, count };
          }
          return item;
        }) ?? [],
    };

    const res = await extensions.wishlist.updateItem({ lineItem: { id: lineItem.lineItemId }, count });

    if (!res.isError) {
      await mutate(
        ['/action/wishlist/getWishlist', account?.accountId, cartData?.cartId],
        { data: res.data },
        {
          optimisticData: { data: newWishlist },
          rollbackOnError: true,
          revalidate: false,
        },
      );
    }
  }, []);

  return {
    ...data,
    totalItems: totalWishlistItems,
    shareWishlist,
    addToWishlist,
    removeLineItem,
    deleteWishlist,
    updateLineItem,
  };
};

export default useWishlist;
